import { Link } from "@tanstack/react-router";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

interface ErrorProps {
	title?: string;
	message?: string;
	retry?: () => void;
}

export function Error404Component() {
	return (
		<div className="flex flex-col items-center justify-center min-h-[80vh] p-8 text-center">
			<h1 className="text-4xl font-bold mb-4">🤷‍♂️ 404 - Page Not Found</h1>

			<div className="mb-8">
				<img
					src="https://http.dog/404.jpg"
					alt="404 dog"
					className="rounded-lg shadow-lg max-w-md mx-auto dark:brightness-90"
				/>
			</div>

			<div className="max-w-lg mx-auto space-y-4">
				<p className="text-xl mb-4">
					Oops! The page you're looking for doesn't exist.
				</p>

				<div className="text-gray-600 dark:text-gray-400">
					<p className="text-left">Here are a few things you can try:</p>
					<ul className="list-disc list-inside mt-2 text-left">
						<li>Check the URL for typos</li>
						<li>Go back to the previous page</li>
						<li>Head to the home page</li>
					</ul>
				</div>
			</div>

			<div className="flex gap-4 justify-center mt-8">
				<Button
					type="button"
					onClick={() => window.history.back()}
					className="bg-gray-200 dark:bg-gray-700 rounded hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
				>
					← Go Back
				</Button>
				<Button
					asChild
					className="bg-blue-500 text-white rounded hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 transition-colors"
				>
					<Link to="/">Home Page</Link>
				</Button>
			</div>
		</div>
	);
}

export function Error500Component({ error }: { error?: unknown }) {
	const detail =
		error instanceof Error
			? error.message
			: typeof error === "string"
				? error
				: undefined;

	useEffect(() => {
		if (error !== undefined) {
			console.error("[route error]", error);
		}
	}, [error]);

	return (
		<div className="flex flex-col items-center justify-center min-h-[80vh] p-8 text-center">
			<h1 className="text-4xl font-bold mb-4">
				🤷‍♂️ 500 - Internal Server Error
			</h1>

			<div className="mb-8">
				<img
					src="https://http.dog/500.jpg"
					alt="500 dog"
					className="rounded-lg shadow-lg max-w-md mx-auto dark:brightness-90"
				/>
			</div>

			<div className="max-w-lg mx-auto space-y-4">
				<p className="text-xl mb-4">Oops! Something went wrong on our end.</p>
				{detail ? (
					<p className="rounded-lg border border-border/80 bg-muted/30 px-4 py-3 text-left font-mono text-sm text-muted-foreground break-words">
						{detail}
					</p>
				) : null}

				<div className="text-gray-600 dark:text-gray-400">
					<p className="text-left">Here are a few things you can try:</p>
					<ul className="list-disc list-inside mt-2 text-left">
						<li>Refresh the page</li>
						<li>Try again in a few minutes</li>
						<li>Go back to the previous page</li>
						<li>Head to the home page</li>
					</ul>
				</div>
			</div>

			<div className="flex gap-4 justify-center mt-8">
				<Button
					type="button"
					onClick={() => window.history.back()}
					className="bg-gray-200 dark:bg-gray-700 rounded hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
				>
					← Go Back
				</Button>
				<Button
					asChild
					className="bg-blue-500 text-white rounded hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 transition-colors"
				>
					<Link to="/">Home Page</Link>
				</Button>
			</div>
		</div>
	);
}

export function GenericError({
	title = "Something went wrong",
	message = "An unexpected error occurred. Please try again later.",
	retry,
}: ErrorProps) {
	return (
		<div className="container flex items-center justify-center min-h-[400px]">
			<Card className="w-full max-w-md">
				<CardHeader>
					<CardTitle className="text-center">{title}</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<p className="text-center text-muted-foreground">{message}</p>
					{retry && (
						<div className="flex justify-center">
							<Button onClick={retry}>Try Again</Button>
						</div>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
